declare global {
  interface Window {
    fbq: any
  }
}

export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID

/**
 * Safe wrapper for fbq calls to handle potential promise rejections
 */
function safeFbq(command: string, ...args: any[]) {
  if (typeof window === 'undefined' || !window.fbq) {
    return Promise.resolve() // Return resolved promise if fbq isn't available
  }

  try {
    // The fbq function might return a promise or undefined
    const result = window.fbq(command, ...args)

    // If it's a promise, catch any rejections
    if (result && typeof result.catch === 'function') {
      return result.catch((error: any) => {
        console.error(`[Analytics] Error in fbq(${command})`, error)
        return undefined // Return a resolved promise to prevent unhandled rejections
      })
    }

    // If it's not a promise, just return a resolved promise
    return Promise.resolve(result)
  } catch (error) {
    console.error(`[Analytics] Exception in fbq(${command})`, error)
    return Promise.resolve() // Return resolved promise to prevent unhandled rejections
  }
}

/**
 * Captures a pageview in Facebook Pixel.
 *
 * DEPRECATED: Use the PageViewTracker component on the home page instead.
 * This should only be used for backward compatibility.
 */
export const pageview = (url: string) => {
  if (typeof window !== 'undefined' && window.fbq) {
    // Get consent information
    const hasMarketingConsent = document.cookie.includes('cookiePreferences=')
      ? JSON.parse(decodeURIComponent(document.cookie.split('cookiePreferences=')[1]?.split(';')[0] || '{}'))?.marketing
      : false

    if (!hasMarketingConsent) {
      console.log('Skipping Facebook pageview due to missing marketing consent')
      return
    }

    // Ensure we're sending absolute URLs
    let fullUrl = url
    if (!url.startsWith('http')) {
      fullUrl = `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`
    }

    // Always include event_source_url parameter to avoid FB blocking
    window.fbq('track', 'PageView', {
      event_source_url: fullUrl,
    })

    console.log(`Facebook Pixel pageview tracked: ${url}`)
  }
}

/**
 * Captures a standard event in Facebook Pixel.
 */
export const event = (name: string, options = {}, eventOptions = {}) => {
  if (window.fbq) {
    safeFbq('track', name, options, eventOptions)
  }
}

/**
 * Captures a custom event in Facebook Pixel.
 */
export const trackCustom = (name: string, options = {}, eventOptions = {}) => {
  if (window.fbq) {
    safeFbq('trackCustom', name, options, eventOptions)
  }
}
